// ─── Shared Role Resolution ──────────────────────────────────────────────────

import {
  normalizeAccessMap,
  ProjectAccessMap,
} from "./projectAccess";

type RoleInput = {
  role?: string;
  permissions?: string;
  id?: string;
  uid?: string;
};

export type RoleFlags = {
  isAdmin: boolean;
  isEditor: boolean;
  isViewer: boolean;
  isNormalUser: boolean;
  currentUserId: string;
};

const noRole: RoleFlags = {
  isAdmin: false,
  isEditor: false,
  isViewer: false,
  isNormalUser: false,
  currentUserId: "",
};

export const resolveRole = (user: any): RoleFlags => {
  if (!user) return noRole;

  const permissions = (user.permissions || "").toLowerCase();
  const role = (user.role || "").toLowerCase();
  const currentUserId: string = user.id || user.uid || "";

  // Org admin bypasses all role checks - gets full admin access
  const isAdmin = !!user.isOrgAdmin || permissions === "admin" || role === "admin";
  const isEditor = !isAdmin && (permissions === "editor" || role === "editor");
  const isViewer = !isAdmin && !isEditor && (permissions === "viewer" || role === "viewer");
  const isNormalUser = !isAdmin && !isEditor && !isViewer;

  return { isAdmin, isEditor, isViewer, isNormalUser, currentUserId };
};

// ─── Project Permissions ─────────────────────────────────────────────────────

export type OwnedProject = {
  createdBy?: string;
  [key: string]: any;
};

export type ProjectPermissions = RoleFlags & {
  canViewAll: boolean;
  canCreate: boolean;
  canEdit: (project: OwnedProject | null | undefined) => boolean;
  canDelete: (project: OwnedProject | null | undefined) => boolean;
  canAssign: boolean;
};

const noProjectAccess: ProjectPermissions = {
  ...noRole,
  canViewAll: false,
  canCreate: false,
  canEdit: () => false,
  canDelete: () => false,
  canAssign: false,
};

export const getProjectPermissions = (currentUser: any): ProjectPermissions => {
  if (!currentUser) return noProjectAccess;

  const { isAdmin, isEditor, isViewer, isNormalUser, currentUserId } =
    resolveRole(currentUser);

  return {
    isAdmin,
    isEditor,
    isViewer,
    isNormalUser,
    currentUserId,
    canViewAll: isAdmin,
    canCreate: isAdmin || isEditor || isNormalUser,
    canAssign: isAdmin,
    canEdit: (project) => {
      if (isAdmin || isEditor) return true;
      if (isViewer) return false;
      if (isNormalUser) {
        return !!project && project.createdBy === currentUserId;
      }
      return false;
    },
    canDelete: (project) => {
      if (isAdmin) return true;
      if (isEditor || isViewer) return false;
      if (isNormalUser) {
        return !!project && project.createdBy === currentUserId;
      }
      return false;
    },
  };
};

// ─── Timesheet Permissions ───────────────────────────────────────────────────

export type OwnedTimesheet = {
  userId?: string;
  [key: string]: any;
};

export type TimesheetPermissions = RoleFlags & {
  canViewAll: boolean;
  canCreate: boolean;
  canEdit: (entry: OwnedTimesheet | null | undefined) => boolean;
  canDelete: (entry: OwnedTimesheet | null | undefined) => boolean;
  canApprove: boolean;
  canExport: boolean;
};

const noTimesheetAccess: TimesheetPermissions = {
  ...noRole,
  canViewAll: false,
  canCreate: false,
  canEdit: () => false,
  canDelete: () => false,
  canApprove: false,
  canExport: false,
};

export const getTimesheetPermissions = (
  currentUser: any
): TimesheetPermissions => {
  if (!currentUser) return noTimesheetAccess;

  const { isAdmin, isEditor, isViewer, isNormalUser, currentUserId } =
    resolveRole(currentUser);

  return {
    isAdmin,
    isEditor,
    isViewer,
    isNormalUser,
    currentUserId,
    canViewAll: isAdmin,
    canCreate: isAdmin || isEditor || isViewer || isNormalUser,
    canEdit: (entry) => {
      if (isAdmin || isEditor || isViewer) return true;
      if (isNormalUser) {
        return !!entry && entry.userId === currentUserId;
      }
      return false;
    },
    canDelete: (entry) => {
      if (isAdmin || isEditor || isViewer) return true;
      if (isNormalUser) {
        return !!entry && entry.userId === currentUserId;
      }
      return false;
    },
    canApprove: isAdmin,
    canExport: true,
  };
};

// ─── Task Permissions ────────────────────────────────────────────────────────

export type OwnedTask = {
  createdBy?: string;
  [key: string]: any;
};

export type TaskPermissions = RoleFlags & {
  canViewAll: boolean;
  canCreate: boolean;
  canEdit: (task: OwnedTask | null | undefined) => boolean;
  canDelete: (task: OwnedTask | null | undefined) => boolean;
};

const noTaskAccess: TaskPermissions = {
  ...noRole,
  canViewAll: false,
  canCreate: false,
  canEdit: () => false,
  canDelete: () => false,
};

export const getTaskPermissions = (currentUser: any): TaskPermissions => {
  if (!currentUser) return noTaskAccess;

  const { isAdmin, isEditor, isViewer, isNormalUser, currentUserId } =
    resolveRole(currentUser);

  return {
    isAdmin,
    isEditor,
    isViewer,
    isNormalUser,
    currentUserId,
    canViewAll: isAdmin,
    // Global viewers are read-only on tasks: they can neither add nor edit,
    // not even tasks they created themselves.
    canCreate: isAdmin || isEditor || isNormalUser,
    canEdit: (task) => {
      if (isAdmin || isEditor) return true;
      if (isViewer) return false;
      if (isNormalUser) {
        return !!task && task.createdBy === currentUserId;
      }
      return false;
    },
    canDelete: (task) => {
      if (isAdmin) return true;
      if (isEditor || isViewer) return false;
      if (isNormalUser) {
        return !!task && task.createdBy === currentUserId;
      }
      return false;
    },
  };
};

// ─── KPI Permissions ─────────────────────────────────────────────────────────

export type OwnedMetric = {
  createdBy?: string;
  assignedTo?: string[];
  [key: string]: any;
};

export type KpiPermissions = RoleFlags & {
  canViewAll: boolean;
  canCreate: boolean;
  canEdit: (metric: OwnedMetric | null | undefined) => boolean;
  canDelete: (metric: OwnedMetric | null | undefined) => boolean;
  canManageUsers: boolean;
};

const noKpiAccess: KpiPermissions = {
  ...noRole,
  canViewAll: false,
  canCreate: false,
  canEdit: () => false,
  canDelete: () => false,
  canManageUsers: false,
};

export const getKpiPermissions = (currentUser: any): KpiPermissions => {
  if (!currentUser) return noKpiAccess;

  const { isAdmin, isEditor, isViewer, isNormalUser, currentUserId } =
    resolveRole(currentUser);

  return {
    isAdmin,
    isEditor,
    isViewer,
    isNormalUser,
    currentUserId,
    canViewAll: isAdmin || isEditor,
    canCreate: isAdmin || isEditor || isNormalUser,
    canEdit: (metric) => {
      if (isAdmin || isEditor) return true;
      if (isViewer || isNormalUser) {
        return !!metric && metric.createdBy === currentUserId;
      }
      return false;
    },
    canDelete: (metric) => {
      return isAdmin;
    },
    canManageUsers: isAdmin,
  };
};

// ─── Project-Scoped Access ───────────────────────────────────────────────────
//
// A user can hold a per-project editor access on top of their org-wide
// permission. If the user's global permission is admin or editor, no project
// level check is needed — they have full access. Otherwise, if the user has
// editor access on a specific project:
//
//   editor → add/edit channels and tasks, edit the project, see only the
//            channels and tasks assigned to them
//
// Deleting is deliberately absent: project-scoped access never grants delete on
// projects, channels or tasks. Delete stays governed purely by the org-wide
// permission (canDelete on the helpers above), which is why nothing here feeds
// into it.

export const getUserProjectAccessMap = (currentUser: any): ProjectAccessMap =>
  normalizeAccessMap(currentUser?.projectAccess);

export type ProjectScopeAccess = RoleFlags & {
  /** Whether the user has editor access on this project. */
  hasEditorAccess: boolean;
  /** Whether an explicit project grant exists. */
  isProjectScoped: boolean;
  /** Whether the user's global permission is admin or editor (bypasses project checks). */
  isGlobalAdminOrEditor: boolean;
  /** Whether the user can see the project. */
  canViewProject: boolean;
  /** Sees every channel in the project rather than only assigned ones. */
  canViewAllChannels: boolean;
  /** Sees every task in a visible channel rather than only assigned ones. */
  canViewAllTasks: boolean;
  /** May add and edit channels in this project. */
  canManageChannels: boolean;
  /** May add and edit tasks in this project. */
  canManageTasks: boolean;
  /** May edit the project itself. */
  canEditProject: boolean;
};

/**
 * Resolves what a user may do inside one specific project, combining their
 * org-wide permission with any project-scoped editor access.
 *
 * If the user's global permission is admin or editor, they bypass project-level
 * checks and get full access. Otherwise, editor access on the project grants
 * add/edit capabilities but limited visibility to assigned items.
 */
export const getProjectScopeAccess = (
  currentUser: any,
  projectId: string | null | undefined
): ProjectScopeAccess => {
  const flags = resolveRole(currentUser);
  const accessMap = getUserProjectAccessMap(currentUser);
  const hasEditorAccess = !!(projectId && accessMap[projectId]);

  const isProjectScoped = hasEditorAccess;
  
  // If the user's global permission is admin or editor, bypass project-level checks
  const isGlobalAdminOrEditor = flags.isAdmin || flags.isEditor;
  
  // For global admin/editor: they see everything and can manage everything
  // For project-scoped editor: they see only assigned items but can add/edit
  const canViewAllChannels = isGlobalAdminOrEditor;
  const canViewAllTasks = isGlobalAdminOrEditor;
  
  // Can manage channels/tasks if global admin/editor OR project-scoped editor
  const canManageChannels = isGlobalAdminOrEditor || hasEditorAccess;
  const canManageTasks = isGlobalAdminOrEditor || hasEditorAccess;
  const canEditProject = isGlobalAdminOrEditor || hasEditorAccess;

  return {
    ...flags,
    hasEditorAccess,
    isProjectScoped,
    isGlobalAdminOrEditor,
    canViewProject: isGlobalAdminOrEditor || isProjectScoped || flags.isAdmin,
    canViewAllChannels,
    canViewAllTasks,
    canManageChannels,
    canManageTasks,
    canEditProject,
  };
};

/** Project ids the user holds an explicit grant on. */
export const getAccessibleProjectIds = (currentUser: any): string[] =>
  Object.keys(getUserProjectAccessMap(currentUser));

/**
 * True when the user should see this project at all — either through the
 * existing assignment lists or through a project-scoped grant.
 */
export const canSeeProject = (
  currentUser: any,
  project: { id: string; assignedUsers?: string[] } | null | undefined,
  opts: { canViewAll?: boolean } = {}
): boolean => {
  if (!project) return false;
  if (opts.canViewAll) return true;

  const userId = currentUser?.id || currentUser?.uid || "";
  if (userId && project.assignedUsers?.includes(userId)) return true;
  if (currentUser?.assignedProjects?.includes(project.id)) return true;

  return !!getUserProjectAccessMap(currentUser)[project.id];
};

/**
 * One row inside a task group's `data[]` array. Assignment is stored here — on
 * the individual rows and their nested subtasks — NOT on the task-group
 * document itself, which only carries { title, description, createdBy, data }.
 */
type TaskRow = {
  assignedUsers?: string[];
  createdBy?: string;
  subtasks?: TaskRow[];
};

/** Depth-first search for any row assigned to (or created by) the user. */
const rowsInvolveUser = (
  rows: TaskRow[] | undefined,
  userId: string
): boolean => {
  if (!rows || rows.length === 0) return false;
  return rows.some(
    (row) =>
      row.createdBy === userId ||
      !!row.assignedUsers?.includes(userId) ||
      rowsInvolveUser(row.subtasks, userId)
  );
};

export type VisibleTaskGroup = {
  assignedUsers?: string[];
  createdBy?: string;
  data?: TaskRow[];
};

/**
 * True when a user may see one task group under project-scoped access.
 * Global admin/editor access sees everything; project-scoped editor grants
 * see a group when they created it, or when any task or subtask inside it
 * is assigned to them.
 */
export const canSeeTaskInProject = (
  access: ProjectScopeAccess,
  task: VisibleTaskGroup | null | undefined
): boolean => {
  if (access.canViewAllTasks) return true;
  if (!task) return false;
  const userId = access.currentUserId;
  if (!userId) return false;

  if (task.createdBy === userId) return true;
  if (task.assignedUsers?.includes(userId)) return true;

  // The real signal: assignment on the rows inside the group.
  return rowsInvolveUser(task.data, userId);
};

/**
 * Filters task data (rows and subtasks) to only include items assigned to
 * the user. Used for project-scoped editor access where users should only
 * see assigned subtasks within a visible task group.
 */
export const filterTaskDataByAssignment = (
  data: TaskRow[] | undefined,
  userId: string
): TaskRow[] => {
  if (!data || data.length === 0) return [];
  return data
    .filter(
      (row) =>
        row.createdBy === userId ||
        row.assignedUsers?.includes(userId)
    )
    .map((row) => ({
      ...row,
      subtasks: filterTaskDataByAssignment(row.subtasks, userId),
    }));
};

// ─── Leave Calendar Permissions ─────────────────────────────────────────────

export type LeaveCalendarPermissions = RoleFlags & {
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
};

const noLeaveCalendarAccess: LeaveCalendarPermissions = {
  ...noRole,
  canCreate: false,
  canEdit: false,
  canDelete: false,
};

export const getLeaveCalendarPermissions = (
  currentUser: any
): LeaveCalendarPermissions => {
  if (!currentUser) return noLeaveCalendarAccess;

  const { isAdmin, isEditor, isViewer, isNormalUser, currentUserId } =
    resolveRole(currentUser);

  return {
    isAdmin,
    isEditor,
    isViewer,
    isNormalUser,
    currentUserId,
    canCreate: isAdmin || isEditor,
    canEdit: isAdmin || isEditor,
    canDelete: isAdmin,
  };
};

// ─── Leave Apply Permissions (Employee Logs → Leave section) ────────────────

export type LeaveApplyPermissions = RoleFlags & {
  canCreate: boolean;
  canEdit: (entry: any) => boolean;
  canDelete: (entry: any) => boolean;
};

const noLeaveApplyAccess: LeaveApplyPermissions = {
  ...noRole,
  canCreate: false,
  canEdit: () => false,
  canDelete: () => false,
};

export const getLeaveApplyPermissions = (
  currentUser: any
): LeaveApplyPermissions => {
  if (!currentUser) return noLeaveApplyAccess;

  const { isAdmin, isEditor, isViewer, isNormalUser, currentUserId } =
    resolveRole(currentUser);

  return {
    isAdmin,
    isEditor,
    isViewer,
    isNormalUser,
    currentUserId,
    canCreate: isAdmin || isEditor || isViewer || isNormalUser,
    canEdit: (entry) => {
      if (isAdmin || isEditor || isViewer) return true;
      if (isNormalUser) {
        return !!entry && entry.userId === currentUserId;
      }
      return false;
    },
    canDelete: (entry) => {
      if (isAdmin || isEditor || isViewer) return true;
      if (isNormalUser) {
        return !!entry && entry.userId === currentUserId;
      }
      return false;
    },
  };
};
